import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import { projects } from '../../mocks/projects';

const HomePage = () => {
  const featuredProjects = projects.slice(0, 6);
  const [scrollY, setScrollY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const businessProcessSteps = [
    { icon: 'ri-map-pin-line', title: '입지 및\n시장 조사' },
    { icon: 'ri-bar-chart-box-line', title: '사업성 분석\n및 기획' },
    { icon: 'ri-building-line', title: '사업 구조\n설계' },
    { icon: 'ri-file-text-line', title: '인·허가 및\n업무대행' },
    { icon: 'ri-store-3-line', title: '분양·마케팅\n및 관리' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 모바일 포트폴리오 자동 슬라이드
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredProjects.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [featuredProjects.length]);

  // 슬라이드 변경 시 스크롤 위치 업데이트
  useEffect(() => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.offsetWidth;
      sliderRef.current.scrollTo({
        left: currentSlide * slideWidth,
        behavior: 'smooth',
      });
    }
  }, [currentSlide]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: '주식회사 GB더플래닛',
    alternateName: '지비더플래닛',
    description: '부동산 개발·시행·업무대행 전문 기업. 기획부터 분양까지 사업 성공의 파트너',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    logo: 'https://static.readdy.ai/image/5e8897340d9d3c287a609e9913af4db2/f044a0b9f809a354f0680c1e52db3d73.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '경인로 40길 25, 3층',
      addressLocality: '구로구',
      addressRegion: '서울특별시',
      addressCountry: 'KR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 37.4959,
      longitude: 126.8871,
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 37.4959,
        longitude: 126.8871,
      },
      geoRadius: '50000',
    },
    serviceType: ['부동산개발', '부동산시행', '업무대행', '분양대행', '광고대행'],
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />

      {/* Hero Section - Image Background */}
      <section className="relative h-[85vh] md:h-[90vh] overflow-hidden">
        {/* Background Image with Panning Animation */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/ce5757ebc8406685265c63943c4736ed.jpg"
            alt="GB the Planet"
            className="w-[120%] h-[120%] object-cover object-center animate-slow-pan"
            style={{
              animation: 'slowPan 25s ease-in-out infinite alternate',
            }}
          />
          {/* Dark Overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 pt-20 md:pt-24 pb-10 md:pb-16 h-full flex flex-col justify-center items-center">
          <div className="text-center">
            <AnimatedSection animation="fadeUp" delay={100}>
              <p 
                className="text-white/70 text-xs md:text-sm font-normal tracking-wide mb-1.5 md:mb-2 font-english"
                style={{
                  animation: 'subtleFloat 4s ease-in-out infinite',
                }}
              >
                Great Business PARTNER
              </p>
            </AnimatedSection>
            <AnimatedSection animation="fadeUp" delay={200}>
              <p 
                className="text-[#D4AF37] text-sm md:text-base mb-3 md:mb-4"
                style={{
                  animation: 'subtlePulse 3s ease-in-out infinite',
                }}
              >
                주식회사 GB더플래닛
              </p>
            </AnimatedSection>
            <AnimatedSection animation="fadeUp" delay={300}>
              <h1
                className="text-5xl md:text-7xl lg:text-[120px] font-black leading-[0.85] mb-6 md:mb-8"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  background: 'linear-gradient(-45deg, #B8922E 0%, #D4AF37 25%, #F5D27A 50%, #D4AF37 75%, #B8922E 100%)',
                  backgroundSize: '200% 200%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 2px 4px rgba(10, 22, 40, 0.9)) drop-shadow(0 4px 8px rgba(10, 22, 40, 0.7)) drop-shadow(0 8px 16px rgba(10, 22, 40, 0.5))',
                  animation: 'shimmer 4s ease-in-out infinite, gentleFloat 6s ease-in-out infinite',
                }}
              >
                GB the Planet
              </h1>
            </AnimatedSection>
            <AnimatedSection animation="fadeUp" delay={400}>
              <div 
                className="text-white text-base md:text-lg leading-relaxed mb-6 md:mb-8 font-normal"
                style={{
                  animation: 'subtleFloat 5s ease-in-out infinite 0.5s',
                }}
              >
                <p>고객과 함께 가치있는 삶을 추구하는 기업</p>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeUp" delay={500}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 md:gap-3">
                <Link
                  to="/portfolio"
                  className="inline-flex items-center justify-center gap-2 px-5 md:px-6 py-2.5 md:py-3 border-2 border-white/40 text-white text-sm font-medium rounded-full hover:bg-white/10 hover:border-white/60 transition-all whitespace-nowrap cursor-pointer"
                >
                  프로젝트 보기
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Company Positioning */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-[800px] mx-auto px-4 md:px-8 text-center">
          <AnimatedSection animation="fadeUp">
            <p className="text-gold text-xs md:text-sm font-semibold tracking-wider mb-2 font-english">OUR BUSINESS</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-4 md:mb-5">
              시장 조사부터 사업 실행까지<br />전 과정 수행
            </h2>
          </AnimatedSection>
          <AnimatedSection animation="fadeUp" delay={200}>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6 md:mb-8">
              <span className="text-gold font-medium">고객 만족과 품질 관리를 최우선 가치로 삼는</span>&nbsp;GB더플래닛은 부동산 개발·시행&nbsp;모든 단계를 책임지고 수행하는 <span className="text-gold font-medium">종합 부동산 개발회사</span> 입니다. 풍부한 경험과 전문성을 바탕으로 <span className="text-gold font-medium">잠재가치를 극대화</span>하고,
생산적·효율적인 부가가치를 창출하여 고객의 <span className="text-gold font-medium">사업 성공을 위한 최적의 솔루션</span>을 제공합니다.
            </p>
          </AnimatedSection>
          <AnimatedSection animation="fadeUp" delay={300}>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-2.5 md:py-3 bg-navy text-white text-sm font-medium rounded-full hover:bg-navy/90 transition-all whitespace-nowrap cursor-pointer"
            >
              READ MORE
              <i className="ri-arrow-right-line"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Specialized Business */}
      <section className="bg-gray-50 py-12 md:py-20 relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left: 특화사업 리스트 */}
            <AnimatedSection animation="fadeRight" delay={100}>
              <div className="space-y-3 md:space-y-4">
                {[
                  { num: '01', text: '아파트 등 주거상품 분양 전문 노하우 보유' },
                  { num: '02', text: '미분양 물량 컨설팅 및 마케팅 경험 풍부' },
                  { num: '03', text: '주상복합, 오피스텔 초반 분양률 제고 노하우 보유' },
                  { num: '04', text: '상업 시설 전략 기획 및 MD 구성, 마케팅 능력 보유' },
                  { num: '05', text: '조기분양 마감 솔루션 컨설팅 및 마케팅 경험 풍부' },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="group relative bg-white rounded-xl p-4 md:p-5 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-gold/30 overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-gold to-gold/50 transform origin-top transition-transform duration-300 group-hover:scale-y-100"></div>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <span 
                          className="text-3xl md:text-4xl font-bold text-gold group-hover:text-gold transition-colors duration-300"
                          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                        >
                          {item.num}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm md:text-base text-gray-700 font-medium group-hover:text-navy transition-colors duration-300">
                          {item.text}
                        </p>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-arrow-right-s-line text-gold text-xl"></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Right: 다이어그램 */}
            <AnimatedSection animation="fadeLeft" delay={200}>
              <div className="relative w-full max-w-[450px] mx-auto aspect-square">
                {/* SVG 다이어그램 */}
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  {/* 연결선들 */}
                  <line x1="200" y1="200" x2="200" y2="60" stroke="#D4AF37" strokeWidth="2" strokeDasharray="5,5" opacity="0.6" />
                  <line x1="200" y1="200" x2="330" y2="130" stroke="#D4AF37" strokeWidth="2" strokeDasharray="5,5" opacity="0.6" />
                  <line x1="200" y1="200" x2="310" y2="290" stroke="#D4AF37" strokeWidth="2" strokeDasharray="5,5" opacity="0.6" />
                  <line x1="200" y1="200" x2="90" y2="290" stroke="#D4AF37" strokeWidth="2" strokeDasharray="5,5" opacity="0.6" />
                  <line x1="200" y1="200" x2="70" y2="130" stroke="#D4AF37" strokeWidth="2" strokeDasharray="5,5" opacity="0.6" />
                  
                  {/* 중앙 원 */}
                  <circle cx="200" cy="200" r="65" fill="#0a1929" stroke="#D4AF37" strokeWidth="3" />
                  <text x="200" y="195" textAnchor="middle" dominantBaseline="middle" fill="#D4AF37" fontSize="18" fontWeight="bold">특화사업</text>
                  <text x="200" y="220" textAnchor="middle" dominantBaseline="middle" fill="#D4AF37" fontSize="16" fontWeight="bold">5대 영역</text>
                  
                  {/* 1. 주거상품 분양 (상단) */}
                  <circle cx="200" cy="60" r="45" fill="white" stroke="#D4AF37" strokeWidth="2.5" />
                  <text x="200" y="52" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">주거상품</text>
                  <text x="200" y="70" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">분양</text>
                  
                  {/* 2. 미분양 컨설팅 (우측 상단) */}
                  <circle cx="330" cy="130" r="45" fill="white" stroke="#D4AF37" strokeWidth="2.5" />
                  <text x="330" y="122" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">미분양</text>
                  <text x="330" y="140" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">컨설팅</text>
                  
                  {/* 3. 주상복합/오피스텔 (우측 하단) */}
                  <circle cx="310" cy="290" r="45" fill="white" stroke="#D4AF37" strokeWidth="2.5" />
                  <text x="310" y="282" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="12" fontWeight="600">주상복합</text>
                  <text x="310" y="300" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="12" fontWeight="600">오피스텔</text>
                  
                  {/* 4. 상업시설 (좌측 하단) */}
                  <circle cx="90" cy="290" r="45" fill="white" stroke="#D4AF37" strokeWidth="2.5" />
                  <text x="90" y="282" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">상업시설</text>
                  <text x="90" y="300" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">전략기획</text>
                  
                  {/* 5. 조기분양 마감 (좌측 상단) */}
                  <circle cx="70" cy="130" r="45" fill="white" stroke="#D4AF37" strokeWidth="2.5" />
                  <text x="70" y="122" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">조기분양</text>
                  <text x="70" y="140" textAnchor="middle" dominantBaseline="middle" fill="#0a1929" fontSize="13" fontWeight="600">마감</text>
                </svg>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Portfolio Highlights */}
      <section className="bg-white py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-6 md:mb-10 gap-3">
            <AnimatedSection animation="fadeRight">
              <h2 className="text-3xl md:text-4xl font-bold text-navy font-english">Portfolio Highlights</h2>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft">
              <Link
                to="/portfolio"
                className="text-gold text-sm font-medium flex items-center gap-2 hover:gap-3 transition-all cursor-pointer"
              >
                전체 실적 보기
                <i className="ri-arrow-right-line"></i>
              </Link>
            </AnimatedSection>
          </div>

          {/* 모바일: 자동 슬라이딩 애니메이션 */}
          <div className="md:hidden overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
              }}
            >
              {featuredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="min-w-full flex-shrink-0 px-1"
                >
                  <div 
                    className={`bg-[#F8F6F1] rounded-2xl overflow-hidden shadow-md transition-all duration-500 ${
                      index === currentSlide ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                    }`}
                  >
                    <div className="relative h-56">
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                        <h3 className="text-white text-lg font-bold mb-1">{project.name}</h3>
                        <p className="text-white/70 text-xs">
                          {project.location} · {project.scale.split('/')[1].trim()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* 슬라이드 인디케이터 */}
            <div className="flex justify-center gap-1.5 mt-4">
              {featuredProjects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
                    index === currentSlide
                      ? 'bg-gold w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-1.5'
                  }`}
                  aria-label={`슬라이드 ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* 데스크톱: 그리드 레이아웃 */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredProjects.map((project, index) => (
              <AnimatedSection key={project.id} animation="fadeUp" delay={index * 100}>
                <div className="bg-[#F8F6F1] rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="relative h-52 md:h-60">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <h3 className="text-white text-lg md:text-xl font-bold mb-1">{project.name}</h3>
                      <p className="text-white/70 text-xs">
                        {project.location} · {project.scale.split('/')[1].trim()}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Know-how */}
      <section id="knowhow" className="bg-gray-50 py-12 md:py-20 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 relative z-10">
          {/* 배경 장식 화살표 그래픽 - 순수 장식용, 클릭 불가 */}
          <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
            {/* 좌측 하단 → 우측 상단 화살표 */}
            <div className="absolute bottom-8 left-0 opacity-[0.05] transform -rotate-45">
              <i className="ri-arrow-right-up-line text-[100px] md:text-[140px] text-navy"></i>
            </div>
            {/* 좌측 상단 → 우측 상단 화살표 */}
            <div className="absolute top-4 left-8 opacity-[0.04] transform -rotate-30">
              <i className="ri-arrow-right-up-line text-[70px] md:text-[100px] text-gold"></i>
            </div>
            {/* 우측 하단 → 좌측 상단 화살표 */}
            <div className="absolute bottom-4 right-0 opacity-[0.05] transform rotate-45">
              <i className="ri-arrow-left-up-line text-[80px] md:text-[120px] text-gold"></i>
            </div>
            {/* 우측 상단 → 좌측 상단 화살표 */}
            <div className="absolute top-12 right-8 opacity-[0.04] transform rotate-30">
              <i className="ri-arrow-left-up-line text-[90px] md:text-[130px] text-navy"></i>
            </div>
            {/* 추가 장식 - 중앙 영역 */}
            <div className="absolute top-1/2 left-1/4 opacity-[0.03] hidden lg:block transform -rotate-45">
              <i className="ri-arrow-right-up-line text-[50px] text-navy"></i>
            </div>
            <div className="absolute top-1/3 right-1/4 opacity-[0.03] hidden lg:block transform rotate-45">
              <i className="ri-arrow-left-up-line text-[40px] text-gold"></i>
            </div>
          </div>

          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gold mb-8 md:mb-10 font-english">
              Know-how
            </h2>
          </AnimatedSection>
          
          {/* 반응형 그리드: 모바일 2열, 태블릿 3열, PC 5열 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {[
              {
                icon: 'ri-line-chart-line',
                title: '정확한 시장 분석',
                desc: '데이터 기반의 철저한 시장 조사와 분석으로 사업 성공의 기반을 마련합니다',
              },
              {
                icon: 'ri-file-list-3-line',
                title: '체계적인 사업성 검토',
                desc: '수익성과 리스크를 면밀히 분석하여 최적의 사업 구조를 설계합니다',
              },
              {
                icon: 'ri-settings-3-line',
                title: '실무 중심의 개발 프로세스',
                desc: '현장 경험을 바탕으로 한 체계적이고 효율적인 프로세스를 운영합니다',
              },
              {
                icon: 'ri-megaphone-line',
                title: '분양 성과 중심 전략',
                desc: '시장 트렌드를 반영한 효과적인 분양 마케팅 전략을 수립합니다',
              },
              {
                icon: 'ri-shield-check-line',
                title: '안정적인 사업 관리',
                desc: '프로젝트 전 과정에 걸친 체계적인 관리로 안정적인 사업 진행을 보장합니다',
              },
            ].map((item, index) => (
              <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
                <div className="h-full bg-white rounded-xl shadow-md p-4 md:p-5 flex flex-col hover:shadow-xl transition-shadow border border-gray-100">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-2 md:mb-3">
                    <i className={`${item.icon} text-2xl md:text-3xl text-gold`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-bold text-navy mb-1.5 md:mb-2 break-words">{item.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed break-words line-clamp-3">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Business Process */}
      <section className="bg-gradient-to-b from-navy to-navy-light py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 md:mb-12 font-english">Business Process</h2>

          {/* 모바일: 오각형 레이아웃 */}
          <div className="md:hidden">
            <div className="relative w-full max-w-[320px] mx-auto" style={{ height: '320px' }}>
              {/* SVG 연결선 */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 320">
                {/* 오각형 연결선 */}
                <path
                  d="M160 30 L290 115 L240 280 L80 280 L30 115 Z"
                  fill="none"
                  stroke="#D4AF37"
                  strokeWidth="2"
                  strokeDasharray="8 4"
                  opacity="0.6"
                />
              </svg>
              
              {/* Step 1 - 상단 중앙 */}
              <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '0px' }}>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center bg-navy">
                      <i className="ri-map-pin-line text-xl text-gold"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-xs">
                      1
                    </div>
                  </div>
                  <div className="text-white text-center text-[10px] font-medium mt-1.5 leading-tight">
                    입지 및<br />시장 조사
                  </div>
                </div>
              </div>

              {/* Step 2 - 우측 상단 */}
              <div className="absolute" style={{ right: '10px', top: '85px' }}>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center bg-navy">
                      <i className="ri-bar-chart-box-line text-xl text-gold"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-xs">
                      2
                    </div>
                  </div>
                  <div className="text-white text-center text-[10px] font-medium mt-1.5 leading-tight">
                    사업성 분석<br />및 기획
                  </div>
                </div>
              </div>

              {/* Step 3 - 우측 하단 */}
              <div className="absolute" style={{ right: '45px', bottom: '15px' }}>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center bg-navy">
                      <i className="ri-building-line text-xl text-gold"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-xs">
                      3
                    </div>
                  </div>
                  <div className="text-white text-center text-[10px] font-medium mt-1.5 leading-tight">
                    사업 구조<br />설계
                  </div>
                </div>
              </div>

              {/* Step 4 - 좌측 하단 */}
              <div className="absolute" style={{ left: '45px', bottom: '15px' }}>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center bg-navy">
                      <i className="ri-file-text-line text-xl text-gold"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-xs">
                      4
                    </div>
                  </div>
                  <div className="text-white text-center text-[10px] font-medium mt-1.5 leading-tight">
                    인·허가 및<br />업무대행
                  </div>
                </div>
              </div>

              {/* Step 5 - 좌측 상단 */}
              <div className="absolute" style={{ left: '10px', top: '85px' }}>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center bg-navy">
                      <i className="ri-store-3-line text-xl text-gold"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-xs">
                      5
                    </div>
                  </div>
                  <div className="text-white text-center text-[10px] font-medium mt-1.5 leading-tight">
                    분양·마케팅<br />및 관리
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 데스크톱: 가로 나열 */}
          <div className="hidden md:flex items-center justify-between max-w-4xl mx-auto">
            {businessProcessSteps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-gold flex items-center justify-center mb-2 md:mb-3 hover:bg-gold/20 transition-colors">
                    <i className={`${step.icon} text-xl md:text-2xl text-gold`}></i>
                  </div>
                  <div className="text-white text-center text-xs md:text-sm font-medium whitespace-pre-line">
                    {step.title}
                  </div>
                </div>
                {index < 4 && (
                  <div className="w-10 md:w-14 h-0.5 border-t-2 border-dashed border-gold mx-2 md:mx-3 mb-10"></div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <Link
              to="/business"
              className="inline-block px-6 md:px-8 py-2.5 md:py-3 border-2 border-white text-white text-sm font-medium rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              자세히 보기
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
